import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
export type ContentBlock = {
  type: 'paragraph';
  text: string;
} | {
  type: 'heading';
  text: string;
} | {
  type: 'blockquote';
  text: string;
} | {
  type: 'list';
  items: string[];
};
export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  category: string;
  date: string;
  image: string;
  excerpt: string;
  content: ContentBlock[];
}
export const BLOG_POSTS: BlogPost[] = [{
  id: 1,
  slug: 'sunrise-in-banff',
  title: 'Sunrise in Banff: A Morning to Remember',
  category: 'Featured Journeys',
  date: 'October 12, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/h58wUekQZycBGPqmB5effo/682effc0ba43af376950f23b_renders_robeta.jpg',
  excerpt: 'Waking up to the first light hitting the peaks of the Rockies is an experience that stays with you forever. Here is how we found the perfect spot.',
  content: [{
    type: 'paragraph',
    text: 'We had been planning this trip for months. The idea was simple: drive from Toronto to Banff National Park in our Mythos 4 Two, taking our time along the Trans-Canada Highway and stopping wherever the landscape called to us.'
  }, {
    type: 'paragraph',
    text: 'The first thing you notice about the Robeta is the silence. Even at highway speeds, the cabin is remarkably quiet thanks to the 25mm Armaflex insulation that wraps the entire vehicle. We could have a normal conversation without raising our voices — something we never managed in our previous camper.'
  }, {
    type: 'heading',
    text: 'Arrival at Lake Louise'
  }, {
    type: 'paragraph',
    text: "We arrived at our campsite near Lake Louise just as the sun was setting behind the peaks. The temperature was already dropping fast — the forecast said -10°C overnight. In any other van, this would have been a concern. But the Robeta's heating system is engineered for exactly this. We set it to 20°C and forgot about it."
  }, {
    type: 'paragraph',
    text: 'The next morning, we woke to the most incredible light hitting the peaks. Golden hour in the Rockies is something everyone should experience at least once. We made coffee in the galley — the two-burner stove and proper countertop space make cooking feel like a pleasure, not a chore — and sat in the doorway watching the world wake up.'
  }, {
    type: 'heading',
    text: 'Designed for the Journey'
  }, {
    type: 'paragraph',
    text: 'What struck us most about this trip was how the van never got in the way of the experience. Everything just worked. The solar panels kept our batteries topped up even in the shorter winter days. The storage was thoughtfully designed so we could find everything without rummaging. The bed was genuinely comfortable — not "comfortable for a van" but actually comfortable.'
  }, {
    type: 'blockquote',
    text: "After two weeks on the road, we returned home feeling more rested than when we left. That's the Robeta difference — it doesn't just take you places, it takes care of you along the way."
  }]
}, {
  id: 2,
  slug: 'coastal-drive-cabot-trail',
  title: 'The Ultimate Coastal Drive: Cabot Trail',
  category: 'Featured Journeys',
  date: 'September 28, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg',
  excerpt: 'Winding roads, ocean vistas, and the freedom of the open road. The Cabot Trail in a Robeta is the definition of touring in style.',
  content: [{
    type: 'paragraph',
    text: 'The Cabot Trail in Nova Scotia is one of those drives that every Canadian should do at least once. A 298-kilometer loop around the northern tip of Cape Breton Island, it winds through the Cape Breton Highlands National Park with views that rival anything in Europe.'
  }, {
    type: 'paragraph',
    text: 'I picked up my Mythos 4 Two from the dealer in Halifax and pointed it northeast. The first thing I noticed was how the van handled on the winding coastal roads. At just over 6 meters, the Mythos is compact enough to feel like driving a large SUV rather than a cumbersome RV. The Mercedes-Benz Sprinter chassis provides a solid, planted feel even on the tightest switchbacks.'
  }, {
    type: 'heading',
    text: 'Handling the Highlands'
  }, {
    type: 'paragraph',
    text: 'The Cabot Trail is famous for its dramatic elevation changes. You climb from sea level to over 500 meters in just a few kilometers, with hairpin turns that would make a sports car driver think twice. The Robeta took it all in stride. The low center of gravity — thanks in part to the lightweight Air Furniture Technology — meant the van never felt top-heavy or unstable.'
  }, {
    type: 'paragraph',
    text: 'I spent three nights parked at different spots along the trail. One night at a provincial campground overlooking the ocean, one at a quiet pull-off near Meat Cove, and one at a proper campsite in Pleasant Bay. Each morning, I woke to a different view but the same feeling of comfort.'
  }, {
    type: 'heading',
    text: 'Interior Comforts'
  }, {
    type: 'paragraph',
    text: "The interior of the Mythos is where Robeta really shines. The HPL surfaces are beautiful and practical — I spilled coffee on the countertop and it wiped clean instantly. The soft-close drawers never rattled, even on the roughest sections of road. And the bathroom, while compact, is genuinely usable — a proper shower with good water pressure and a toilet that doesn't feel like an afterthought."
  }, {
    type: 'blockquote',
    text: "If you're considering the Cabot Trail in a Robeta, my advice is simple: do it. Take your time. Stop at every viewpoint. And let the van take care of the rest."
  }]
}, {
  id: 3,
  slug: 'desert-solitude-joshua-tree',
  title: 'Finding Solitude in Joshua Tree',
  category: 'Featured Journeys',
  date: 'November 5, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp',
  excerpt: 'Three days off-grid with zero compromises. How the solar and water systems of the Apollo made desert living effortless.',
  content: [{
    type: 'paragraph',
    text: "There's something about the desert that strips everything back to essentials. No cell service, no schedules, no noise — just you, the landscape, and the stars. Joshua Tree National Park in California is one of the best places in North America to experience this kind of solitude, and our Robeta Mythos 4 Two made it possible to do so in complete comfort."
  }, {
    type: 'paragraph',
    text: "We drove down from Los Angeles on a Friday afternoon, arriving at our BLM dispersed camping spot just south of the park as the sun was setting. The beauty of the Robeta's off-grid capability is that you don't need hookups. The 400W solar array, combined with the 200Ah lithium battery bank, gives you genuine energy independence."
  }, {
    type: 'heading',
    text: 'Powering Remote Work'
  }, {
    type: 'paragraph',
    text: 'Over three days, we never once worried about power. I work remotely as a designer, and I was able to run my laptop, charge my phone, and even power a small monitor — all from solar. The 2000W inverter handles everything without breaking a sweat. In the evenings, we ran the LED lighting, charged our cameras, and watched a movie on the laptop. The battery never dropped below 60%.'
  }, {
    type: 'paragraph',
    text: "The desert gets cold at night — much colder than most people expect. Temperatures dropped to around 2°C on our second night. The Robeta's Truma heating system kept the interior at a comfortable 19°C all night. Combined with the Armaflex insulation, the van retains heat remarkably well. We slept under a single duvet and were perfectly warm."
  }, {
    type: 'heading',
    text: 'Hiking and Homecoming'
  }, {
    type: 'paragraph',
    text: "During the days, we hiked through the park's iconic boulder formations and Joshua tree forests. Coming back to the van after a long hike felt like returning to a small luxury hotel. A hot shower, a proper meal cooked on the two-burner stove, and a comfortable bed — these things matter when you're spending extended time off-grid."
  }, {
    type: 'blockquote',
    text: "The Robeta isn't just a vehicle for getting to remote places. It's a vehicle for living in remote places. And that distinction makes all the difference."
  }]
}, {
  id: 4,
  slug: 'robeta-caravan-salon-dusseldorf-2025',
  title: 'Robeta at Caravan Salon Düsseldorf 2025',
  category: 'News & Events',
  date: 'August 28, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/vmYVR1ocqDdCjuv6EsbcSm/robeta-at-caravan-salon-dusseldorf.jpg',
  excerpt: 'Robeta returns to the world stage at Caravan Salon 2025 with exciting new innovations, expanded North American lineup, and a vision for the future of premium van life.',
  content: [{
    type: 'paragraph',
    text: 'Robeta returned to Düsseldorf this year with a larger presence than ever before, marking our continued growth in the global camper van market. The 2025 Caravan Salon was the perfect stage to unveil our expanded North American lineup, specifically tailored to the unique needs of US and Canadian travelers.'
  }, {
    type: 'heading',
    text: 'North American Innovations'
  }, {
    type: 'paragraph',
    text: 'Central to our display was the new North American spec package for the Mythos 4 Two. This includes a revised suspension system tuned specifically for North American road conditions, offering greater compliance on rougher surfaces without sacrificing high-speed stability. We also showcased our enhanced electrical management system, which now offers full app-based control of all vehicle systems, from lighting to tank monitoring.'
  }, {
    type: 'paragraph',
    text: 'Visitor engagement was at an all-time high, with record numbers of attendees visiting the Robeta stand. The response to our Air Furniture Technology continues to be overwhelmingly positive, with many customers citing the weight savings and durability as key factors in their decision-making process.'
  }, {
    type: 'heading',
    text: 'Looking Ahead'
  }, {
    type: 'paragraph',
    text: 'We also used the event to announce several key partnerships that will expand our dealer and service network across North America, ensuring that Robeta owners have access to premium support wherever their adventures take them. As we look ahead to the 2026 model year, the feedback gathered here in Düsseldorf will be instrumental in shaping the next generation of Robeta vehicles.'
  }, {
    type: 'blockquote',
    text: 'Innovation is not a destination, but a journey. At Robeta, we are constantly moving forward, driven by the passion of our community and the freedom of the open road.'
  }]
}, {
  id: 5,
  slug: 'trust-your-dreams-noemi-zonta-van-life',
  title: 'Trust Your Dreams: How Noemi Žonta Found Freedom in Van Life',
  category: 'Owner Stories',
  date: 'March 15, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/3s99mbkb1hSUMu4sSEz7hA/trust-your-dreams-how-noemi-zonta-found-freedom-in-van-life.jpg',
  excerpt: 'From a corporate career in Ljubljana to full-time van life across Europe. Noemi shares how trusting her instincts led to the adventure of a lifetime.',
  content: [{
    type: 'paragraph',
    text: 'Like many people, Noemi Žonta spent years building a successful corporate career in Ljubljana. But despite the professional achievements, she felt a growing disconnect between her daily life and her true passions. It started with scrolling through van life accounts on social media — a form of escapism that slowly transformed into a concrete plan.'
  }, {
    type: 'paragraph',
    text: 'The turning point came when she attended a dealer event and stepped inside a Robeta for the first time. "It didn\'t feel like a vehicle," she recalls. "It felt like a small, perfectly designed apartment." That day, the dream stopped being abstract. She placed an order for a Mythos 4 Two and began the process of untangling herself from her corporate life.'
  }, {
    type: 'heading',
    text: 'The First Month'
  }, {
    type: 'paragraph',
    text: 'Transitioning to full-time van life wasn\'t without its challenges. The first month was a crash course in water management, power consumption, and route planning. But as she traveled through Slovenia, Croatia, and into Italy, the rhythm of the road began to take hold. "You learn to listen to what you actually need, rather than what society tells you to want," she says.'
  }, {
    type: 'paragraph',
    text: "Now working remotely as a freelance photographer, Noemi uses her van as a mobile studio. The Mythos 4 Two's workspace setup allows her to edit photos in the most inspiring locations imaginable, from the Dolomites to the Amalfi Coast."
  }, {
    type: 'heading',
    text: 'Advice for Solo Travelers'
  }, {
    type: 'paragraph',
    text: 'For other women considering solo van life, Noemi\'s advice is simple: trust yourself. "The fear is always louder before you start," she explains. "Once you\'re out there, you realize how capable you are. The community is incredibly supportive, and the freedom is worth every moment of uncertainty."'
  }, {
    type: 'blockquote',
    text: "The van didn't just change how I travel — it changed how I think about what I need. I have less stuff now, but I have so much more life."
  }]
}, {
  id: 6,
  slug: 'weekend-that-changed-everything',
  title: 'The Weekend That Changed Everything: Finding Adventure in Downsizing',
  category: 'Owner Stories',
  date: 'April 22, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/f7BYubuNomKYP7Ka2kvMTc/the-weekend-that-changed-everything-finding-adventure-in-downsizing.jpg',
  excerpt: 'A couple from Vancouver discovers that letting go of their 3-bedroom house was the beginning of their greatest adventure together.',
  content: [{
    type: 'paragraph',
    text: 'Mark and Lisa, a couple in their mid-40s from Vancouver, were living the standard North American dream: a nice 3-bedroom house, two cars, and stable jobs. But they were also living with the standard North American stress: a large mortgage, endless home maintenance, and the feeling that life was passing them by while they were stuck in traffic.'
  }, {
    type: 'paragraph',
    text: 'It started with a rental. On a whim, they rented a camper van for a long weekend in Tofino. That three-day trip changed the trajectory of their lives. "We realized we were happier with a backpack of clothes and a view of the ocean than we were in our house full of stuff," Mark says.'
  }, {
    type: 'heading',
    text: 'The Decision to Downsize'
  }, {
    type: 'paragraph',
    text: 'Six months later, the house was sold, and they were picking up the keys to their new Robeta. The downsizing process was intense — getting rid of 20 years of accumulated possessions forced them to evaluate what actually mattered. But the financial freedom that came with it was immediate.'
  }, {
    type: 'paragraph',
    text: 'Their first year on the road took them from British Columbia down the Pacific Coast Highway to Baja, Mexico. They discovered that living in a smaller space actually expanded their world. "When your living room is the beach, or the mountains, or a redwood forest, you don\'t miss the square footage," Lisa explains.'
  }, {
    type: 'heading',
    text: 'Strengthening Bonds'
  }, {
    type: 'paragraph',
    text: 'Living in a van also strengthened their relationship. "You can\'t hide in a van," Mark laughs. "You have to communicate, you have to work as a team." They found that the shared challenges and adventures brought them closer together than they had been in years.'
  }, {
    type: 'blockquote',
    text: "We didn't downsize our life — we upgraded it. We traded square footage for mileage, and possessions for experiences. It was the best trade we ever made."
  }]
}, {
  id: 7,
  slug: 'breaking-rules-after-60-silver-rebellion',
  title: 'Breaking the Rules After 60: Adventures in the Silver Rebellion',
  category: 'Owner Stories',
  date: 'May 10, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp',
  excerpt: "Retirement doesn't mean slowing down. Meet the growing community of over-60 adventurers who are rewriting the rules of what comes next.",
  content: [{
    type: 'paragraph',
    text: 'There is a quiet revolution happening on the highways of Europe and North America. It\'s called the "Silver Rebellion," and it\'s led by retirees who are rejecting the traditional rocking-chair retirement in favor of adventure, exploration, and van life.'
  }, {
    type: 'paragraph',
    text: 'Hans (67) and Brigitte (64) from Austria are perfect examples. After retiring, they sold their apartment and moved full-time into a Robeta. "People told us we were crazy," Brigitte says. "They said we should be settling down. But we feel like we are just getting started."'
  }, {
    type: 'heading',
    text: 'Why Robeta?'
  }, {
    type: 'paragraph',
    text: 'For older travelers, comfort and safety are paramount. Hans and Brigitte chose Robeta specifically for its build quality and ergonomics. "The bed is better than the one we had in our apartment," Hans notes. "And the insulation means we are comfortable in any weather. At our age, you don\'t want to be shivering in a sleeping bag."'
  }, {
    type: 'paragraph',
    text: 'They also appreciate the accessibility features. The layout of the Mythos 4 Two is open and easy to navigate, with thoughtful touches like well-placed grab handles and lighting that makes moving around at night safe and easy.'
  }, {
    type: 'heading',
    text: 'Health and Community'
  }, {
    type: 'paragraph',
    text: 'The health benefits of their new lifestyle have been remarkable. "We walk more, we are outside every day, we meet new people constantly," Brigitte says. They have found a vibrant community of other senior van lifers, sharing tips, meals, and stories at campsites across the continent.'
  }, {
    type: 'blockquote',
    text: "At 67, I wake up more excited about the day ahead than I did at 30. Retirement isn't the end of the road — it's just a new map."
  }]
}, {
  id: 8,
  slug: 'pack-like-a-pro-wild-camping-gear',
  title: 'Pack Like a Pro: Essential Gear for Wild Camping Adventures',
  category: 'Van Life Tips',
  date: 'June 5, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/7z3is2BCgabuUutCmURgeJ/_DSC8265.webp',
  excerpt: 'The definitive packing guide for wild camping in your Robeta. What to bring, what to leave behind, and how to organize it all.',
  content: [{
    type: 'paragraph',
    text: "Packing for a wild camping trip is an art form. Bring too little, and you're uncomfortable; bring too much, and you're cluttered. The key is to pack light but smart, focusing on multi-purpose items and quality gear that won't let you down."
  }, {
    type: 'heading',
    text: 'Safety First'
  }, {
    type: 'paragraph',
    text: "Before you pack the fun stuff, ensure your safety kit is complete. This includes a comprehensive first aid kit, a fire extinguisher (accessible instantly), and an emergency beacon or satellite messenger if you're heading out of cell range. A good offline map app like Gaia GPS or OnX is also essential."
  }, {
    type: 'heading',
    text: 'Kitchen Essentials'
  }, {
    type: 'paragraph',
    text: "In the galley, less is more. A high-quality cast iron pan can cook almost anything and lasts forever. Pair it with one good chef's knife, a collapsible pot, and a nesting bowl set. Don't forget a spice kit — small containers of your favorite spices can transform a basic camp meal into a feast."
  }, {
    type: 'heading',
    text: 'Comfort and Tech'
  }, {
    type: 'paragraph',
    text: "For comfort, invest in quality camp chairs — you'll spend a lot of time in them. A good headlamp is indispensable for late-night tasks. On the tech side, even with Robeta's solar system, a portable power bank is great for charging phones on hikes. If you need internet, a Starlink Mini or a signal booster is a game-changer."
  }, {
    type: 'heading',
    text: 'The "One In, One Out" Rule'
  }, {
    type: 'paragraph',
    text: 'To keep your van organized, adopt the "one in, one out" rule. If you buy a new piece of gear, something else has to leave. This prevents the gradual accumulation of clutter that can make van life feel cramped. Robeta\'s storage solutions are generous, but they work best when not overstuffed.'
  }, {
    type: 'blockquote',
    text: "The best gear is the gear you actually use. If you haven't touched it in two trips, leave it at home."
  }]
}, {
  id: 9,
  slug: 'wild-camping-etiquette-guide',
  title: 'Off-Grid Freedom: The Essential Guide to Wild Camping Etiquette',
  category: 'Van Life Tips',
  date: 'July 18, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/1cF7AjC6Vzw1JnKErjYpH6/off-grid-freedom-the-essential-guide-to-wild-camping-etiquette.jpg',
  excerpt: 'Freedom comes with responsibility. A comprehensive guide to wild camping etiquette that ensures beautiful spots stay beautiful for everyone.',
  content: [{
    type: 'paragraph',
    text: 'Wild camping is a privilege, not a right. As the van life community grows, so does our collective responsibility to protect the places we love. Following proper etiquette ensures that these beautiful spots remain open and accessible for everyone.'
  }, {
    type: 'heading',
    text: 'Leave No Trace'
  }, {
    type: 'paragraph',
    text: 'The golden rule is simple: Leave No Trace. This means packing out everything you pack in — including organic waste like orange peels or pistachio shells, which take years to decompose. If you see trash left by others, pick it up. Leave the spot better than you found it.'
  }, {
    type: 'heading',
    text: 'Waste Management'
  }, {
    type: 'paragraph',
    text: 'Never, ever dump grey or black water on the ground. Robeta vans are equipped with sealed waste tanks for a reason. Find a proper dump station. For trash, minimize packaging before you leave home to reduce what you have to carry.'
  }, {
    type: 'heading',
    text: 'Respecting Others'
  }, {
    type: 'paragraph',
    text: "Noise travels further than you think in the wilderness. Keep music low or use headphones, and be mindful of generator use (though with Robeta's solar systems, you shouldn't need one!). Give other campers space — don't park right next to someone if there is plenty of room elsewhere."
  }, {
    type: 'heading',
    text: 'Fire Safety'
  }, {
    type: 'paragraph',
    text: 'Always check local fire restrictions before starting a campfire. If fires are allowed, use existing fire rings and ensure the fire is completely out — cold to the touch — before you sleep or leave. In many areas, a propane fire pit is a safer and cleaner alternative.'
  }, {
    type: 'blockquote',
    text: 'We are stewards of the wild. Every time we camp responsibly, we help ensure that future generations can enjoy the same freedom we do today.'
  }]
}, {
  id: 10,
  slug: 'prepare-campervan-for-winter-storage',
  title: 'Prepare Your Campervan for Winter Storage',
  category: 'Van Life Tips',
  date: 'November 15, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/wMfcP2rkaTe89Xk3t5eCLt/prepare-your-campervan-for-winter-storage.webp',
  excerpt: 'As the season winds down, proper winterization protects your investment and ensures a smooth start next spring. Here is your complete guide.',
  content: [{
    type: 'paragraph',
    text: 'For many van owners, winter means a pause in adventures. Whether you are storing your Robeta for a few weeks or several months, proper preparation is essential to protect your vehicle and ensure it is ready to roll when spring arrives.'
  }, {
    type: 'heading',
    text: 'The Water System'
  }, {
    type: 'paragraph',
    text: 'The most critical step is draining the entire water system. Frozen water expands and can crack pipes, damage the pump, and ruin the boiler. Start by opening all taps and draining the fresh water tank completely. Then drain the boiler and disconnect any water filters. Use compressed air to blow out remaining water from the lines if temperatures will drop below freezing.'
  }, {
    type: 'heading',
    text: 'Battery Care'
  }, {
    type: 'paragraph',
    text: 'Lithium batteries, like those in the Robeta, should ideally be stored at around 50-60% charge in a cool, dry environment. If your van will be parked where it receives sunlight, the solar panels can help maintain charge levels. Disconnect any unnecessary loads and consider using a battery maintainer if the van will be in a garage without solar access.'
  }, {
    type: 'heading',
    text: 'Interior Protection'
  }, {
    type: 'paragraph',
    text: 'Moisture is the enemy during storage. Leave cabinet doors and drawers slightly open to promote air circulation. Place moisture absorbers in key areas — the bathroom, under the bed, and in the kitchen. Remove all perishable food and give the interior a thorough clean. The HPL surfaces in a Robeta are resistant to mould, but prevention is always better than cure.'
  }, {
    type: 'heading',
    text: 'Exterior and Mechanical'
  }, {
    type: 'paragraph',
    text: 'Wash the exterior thoroughly, including the undercarriage, to remove road salt and grime. Check tyre pressures and consider moving the van slightly every few weeks to prevent flat spots. If possible, use a breathable cover rather than a tarp, which can trap moisture against the bodywork.'
  }, {
    type: 'blockquote',
    text: 'A few hours of preparation now saves days of repair later. Treat your van well over winter, and it will reward you with a perfect start to the next season.'
  }]
}, {
  id: 11,
  slug: '7-gifts-for-campervan-lovers',
  title: '7 Gifts for Campervan Lovers',
  category: 'Van Life Tips',
  date: 'December 10, 2025',
  image: 'https://cdn.magicpatterns.com/uploads/fjPgxz2redTt8tycZyuzvA/7-gifts-for-campervan-lovers.png',
  excerpt: 'Searching for the perfect gift for the van lifer in your life? From practical gear to experience-based presents, here are our top seven picks.',
  content: [{
    type: 'paragraph',
    text: 'Finding the right gift for someone who lives in (or dreams of) a campervan can be tricky. They value experiences over things, and space is at a premium. Here are seven thoughtful gifts that any van life enthusiast will genuinely appreciate.'
  }, {
    type: 'heading',
    text: '1. A Quality Enamel Mug'
  }, {
    type: 'paragraph',
    text: "Nothing says van life like a morning coffee with a view. A durable enamel mug with a clever van-related slogan is both practical and personal. It won't break when it inevitably falls off the counter on a bumpy road, and it doubles as a conversation starter at campsites."
  }, {
    type: 'heading',
    text: '2. Trade Fair Tickets'
  }, {
    type: 'paragraph',
    text: 'A ticket to a major caravan show like CMT Stuttgart or the Caravan Salon in Düsseldorf is the gift of inspiration. They can explore the latest innovations, sit in new models, and connect with the community. It is a day out that fuels the dream.'
  }, {
    type: 'heading',
    text: '3. A Leather-Bound Travel Journal'
  }, {
    type: 'paragraph',
    text: "In an age of digital everything, a beautiful handmade travel journal is a luxury. Perfect for sketching routes, noting campsite coordinates, or simply reflecting on the day's adventures. Choose one with quality paper and a durable cover that ages gracefully."
  }, {
    type: 'heading',
    text: '4. A Van Life T-Shirt'
  }, {
    type: 'paragraph',
    text: 'A comfortable, well-designed t-shirt with a witty van life reference is always welcome. Look for soft organic cotton and designs that are clever rather than cliché. Bonus points if it references a specific van culture inside joke.'
  }, {
    type: 'heading',
    text: '5. Campsite Gift Cards'
  }, {
    type: 'paragraph',
    text: 'Gift cards for popular camping networks or apps give the freedom to choose their own adventure. It is practical, always the right size, and guarantees they will think of you while parked somewhere beautiful.'
  }, {
    type: 'heading',
    text: '6. A Handmade Map Print'
  }, {
    type: 'paragraph',
    text: 'A custom map print of a meaningful location — their first road trip destination, a favourite campsite, or the place they got engaged — makes a deeply personal gift that can hang in the van or at home.'
  }, {
    type: 'heading',
    text: '7. A Robeta Experience Day'
  }, {
    type: 'paragraph',
    text: 'For the ultimate gift, book a Robeta discovery experience. A private tour of the vehicle, a test drive through scenic routes, and a taste of what premium van life feels like. It is the kind of gift that can change a life.'
  }, {
    type: 'blockquote',
    text: 'The best gifts for van lifers are not things that fill space — they are things that fuel adventures.'
  }]
}, {
  id: 12,
  slug: 'configure-your-robeta',
  title: 'Configure Your Robeta: The Online Configurator Is Here',
  category: 'News & Events',
  date: 'January 20, 2026',
  image: 'https://cdn.magicpatterns.com/uploads/bdg65WMa1gkKFwz4FwX5De/configure-your-robeta.png',
  excerpt: 'Design your dream Robeta from anywhere in the world. Our new online configurator lets you customize every detail and see your vision come to life.',
  content: [{
    type: 'paragraph',
    text: 'We are thrilled to announce the launch of the Robeta Online Configurator — a powerful new tool that puts the entire customization process at your fingertips. Whether you are on your laptop at home or browsing on your phone at a coffee shop, you can now design your perfect Robeta from anywhere.'
  }, {
    type: 'heading',
    text: 'Build It Your Way'
  }, {
    type: 'paragraph',
    text: 'The configurator walks you through every decision point: base model selection, interior colour schemes, upholstery materials, kitchen configurations, electrical packages, and optional extras. Each choice updates a real-time visualization so you can see exactly how your selections come together.'
  }, {
    type: 'paragraph',
    text: 'We have invested heavily in making the experience intuitive and informative. Every option includes detailed descriptions, high-resolution images, and pricing information. You can compare different configurations side by side and save your favourites to revisit later.'
  }, {
    type: 'heading',
    text: 'From Screen to Reality'
  }, {
    type: 'paragraph',
    text: 'Once you have created your ideal configuration, you can share it directly with your nearest Robeta dealer for a detailed quote. The configurator generates a comprehensive specification sheet that serves as the starting point for your order. Your dealer can then refine the details and guide you through any additional customization options.'
  }, {
    type: 'paragraph',
    text: 'The tool is available on desktop, tablet, and mobile, with a responsive design that works beautifully on any screen size. We have also ensured it works offline once loaded, so you can tinker with configurations even without an internet connection.'
  }, {
    type: 'blockquote',
    text: 'Your Robeta should be as unique as you are. The configurator is the first step in making that vision a reality.'
  }]
}, {
  id: 13,
  slug: 'cmt-stuttgart-2026',
  title: 'Robeta at CMT Stuttgart 2026: Step In, Leave the Crowds Behind',
  category: 'News & Events',
  date: 'January 25, 2026',
  image: 'https://cdn.magicpatterns.com/uploads/bfhjniTytkPJGrRtTY2vM3/cmt-stuttgart-2026.jpg',
  excerpt: 'Robeta made a bold statement at CMT Stuttgart 2026 with private discovery sessions, new model previews, and an invitation to experience van life on your own terms.',
  content: [{
    type: 'paragraph',
    text: 'CMT Stuttgart is one of Europe\'s most important tourism and leisure trade fairs, and for 2026, Robeta brought something different to the show floor. Rather than competing for attention in the usual trade fair chaos, we invited visitors to "Step In, Leave the Crowds Behind" — offering private 20-minute discovery sessions with our vehicles.'
  }, {
    type: 'heading',
    text: 'A Different Kind of Experience'
  }, {
    type: 'paragraph',
    text: 'The concept was simple but powerful: instead of jostling through packed exhibition halls, visitors could book a dedicated time slot for a one-on-one tour of the Mythos 4 Two. A Robeta specialist guided each session, answering questions, demonstrating features, and allowing guests to experience the interior without the usual trade fair rush.'
  }, {
    type: 'paragraph',
    text: 'The response was overwhelming. Sessions were fully booked within days of the announcement, and we added additional time slots to meet demand. Visitors consistently told us it was the best trade fair experience they had ever had — unhurried, personal, and genuinely informative.'
  }, {
    type: 'heading',
    text: 'New Models and Features'
  }, {
    type: 'paragraph',
    text: 'Beyond the discovery sessions, we used CMT Stuttgart to preview several exciting developments for the 2026 model year. Visitors got an early look at new interior configurations, updated technology packages, and refinements to our signature Air Furniture system that further reduce weight while improving durability.'
  }, {
    type: 'paragraph',
    text: 'The Try & Buy programme also generated significant interest, offering potential customers a 3-day trial experience for a modest fee — fully refundable against a purchase. It is the ultimate test drive, allowing people to live with the vehicle before committing.'
  }, {
    type: 'blockquote',
    text: 'We believe buying a campervan should feel as special as owning one. CMT Stuttgart 2026 was our way of proving that.'
  }]
}, {
  id: 14,
  slug: '5-ways-we-perfected-comfort',
  title: '5 Ways We Perfected Comfort in the Mythos 4 Two',
  category: 'Design & Innovation',
  date: 'February 8, 2026',
  image: 'https://cdn.magicpatterns.com/uploads/qjB7RYw9PrDNr2c82otzto/5-ways-we-perfected-comfort.jpg',
  excerpt: 'Comfort in a campervan is not a luxury — it is a necessity. Here are five engineering decisions that make the Mythos 4 Two feel like home.',
  content: [{
    type: 'paragraph',
    text: 'When you spend extended time living in a vehicle, comfort is not optional — it is the foundation of the entire experience. At Robeta, we have obsessed over every detail that contributes to how the Mythos 4 Two feels to live in. Here are five key areas where we went further than the competition.'
  }, {
    type: 'heading',
    text: '1. The Kitchen That Works'
  }, {
    type: 'paragraph',
    text: 'Our galley kitchen is designed by people who actually cook. The two-burner stove is positioned at a comfortable working height. The countertop provides genuine preparation space — not just a token surface. The sink is deep enough to be useful, and the tap has a pull-out spray function. Soft-close drawers keep everything organized and silent on the move.'
  }, {
    type: 'heading',
    text: '2. Climate Control That Adapts'
  }, {
    type: 'paragraph',
    text: 'The Truma heating system combined with 25mm Armaflex insulation creates an interior climate that remains comfortable from -20°C to +40°C. The multi-zone ventilation system allows you to direct airflow exactly where you need it, and the MaxxAir fans provide silent, efficient cooling without the need for power-hungry air conditioning.'
  }, {
    type: 'heading',
    text: '3. A Bed You Look Forward To'
  }, {
    type: 'paragraph',
    text: 'We use a custom-designed mattress with differentiated comfort zones — firmer support where your body needs it, softer cushioning where it matters. The bed platform is slatted for ventilation, preventing moisture buildup underneath. And the dimensions are generous enough for two adults to sleep comfortably without compromise.'
  }, {
    type: 'heading',
    text: '4. A Bathroom That Functions'
  }, {
    type: 'paragraph',
    text: 'The bathroom in the Mythos 4 Two is compact but complete. A proper shower with consistent water pressure, a toilet that does not feel like an afterthought, and a vanity with storage. The key is the engineering: the shower drains efficiently, the ventilation prevents moisture issues, and the materials are chosen to resist mould and staining.'
  }, {
    type: 'heading',
    text: '5. Sound and Light'
  }, {
    type: 'paragraph',
    text: 'The multi-zone LED lighting system transforms the interior from a bright, functional workspace during the day to a warm, intimate living space at night. Combined with the exceptional sound insulation — the result of our comprehensive Armaflex treatment — the interior feels genuinely peaceful, even parked beside a busy road.'
  }, {
    type: 'blockquote',
    text: 'Comfort is not one big thing — it is a thousand small things done right. That is the Robeta philosophy.'
  }]
}, {
  id: 15,
  slug: 'robeta-campervan-innovations-2026',
  title: 'Robeta Campervan Innovations for 2026',
  category: 'News & Events',
  date: 'February 20, 2026',
  image: 'https://cdn.magicpatterns.com/uploads/i3Cy3cRD8TuGLhF5vQXvqT/robeta-campervan-innovations-for-2026.png',
  excerpt: 'A look at the engineering breakthroughs and design refinements coming to the Robeta lineup for the 2026 model year.',
  content: [{
    type: 'paragraph',
    text: 'Every model year at Robeta is an opportunity to push further. For 2026, we have focused on refinements that our owners have been asking for, combined with engineering innovations that set new benchmarks in the Class B campervan segment.'
  }, {
    type: 'heading',
    text: 'Enhanced Chassis Options'
  }, {
    type: 'paragraph',
    text: 'For 2026, we are expanding our chassis offerings to include configurations specifically optimized for different use cases. The touring package prioritizes highway comfort and fuel efficiency, while the adventure package adds enhanced ground clearance and all-terrain capability. Both retain the low center of gravity that makes every Robeta feel planted and secure.'
  }, {
    type: 'heading',
    text: 'Next-Generation Electrical'
  }, {
    type: 'paragraph',
    text: 'Our electrical system receives a significant upgrade for 2026. Increased solar capacity, a larger lithium battery bank, and a smarter management system that learns your usage patterns and optimizes energy distribution accordingly. The companion app now provides real-time monitoring and remote control of all vehicle systems.'
  }, {
    type: 'heading',
    text: 'Interior Refinements'
  }, {
    type: 'paragraph',
    text: 'New interior colour palettes have been developed in collaboration with a leading European design studio, offering fresh options that complement the natural landscapes our owners love to explore. We have also refined the Air Furniture construction process, achieving an additional 8% weight reduction while maintaining the same exceptional durability.'
  }, {
    type: 'heading',
    text: 'Expanded Dealer Network'
  }, {
    type: 'paragraph',
    text: 'Perhaps most importantly for our North American customers, 2026 sees a significant expansion of our dealer and service network. New partnerships in key regions ensure that Robeta owners have access to expert support and maintenance wherever their travels take them.'
  }, {
    type: 'blockquote',
    text: 'Innovation at Robeta is never change for the sake of change. Every improvement must make the experience of living on the road measurably better.'
  }]
}];
const CATEGORIES = ['All', 'Featured Journeys', 'Owner Stories', 'News & Events', 'Design & Innovation', 'Van Life Tips'];
export function BlogPage() {
  useSEO({
    title: 'The Journal — Stories from the Road',
    description: 'Explore the Robeta Journal. Featuring owner stories, van life tips, design insights, and the latest news from Robeta Camper Vans.'
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredPosts = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(post => post.category === activeCategory);
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-357">
      <section className="container mx-auto px-6 md:px-12 mb-16" data-id="element-358">
        <Breadcrumbs items={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Journal'
      }]} className="mb-8" data-id="element-359" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-360">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-361">
            The Journal
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-6" data-id="element-362">
            Stories from the Road
          </h1>
          <p className="text-lg md:text-xl font-light text-robeta-black/60 max-w-2xl leading-relaxed" data-id="element-363">
            Inspiration, innovation, and adventures from the Robeta community.
          </p>
        </motion.div>
      </section>

      <section className="container mx-auto px-6 md:px-12 mb-12 border-b border-robeta-black/10 pb-8" data-id="element-364">
        <div className="flex flex-wrap gap-4" data-id="element-365">
          {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-6 py-2 text-xs font-bold uppercase tracking-widest border transition-all duration-300 ${activeCategory === cat ? 'bg-robeta-black text-white border-robeta-black' : 'bg-white text-robeta-black border-robeta-black/10 hover:border-robeta-copper hover:text-robeta-copper'}`} data-id="element-366">
              {cat}
            </button>)}
        </div>
      </section>

      <section className="container mx-auto px-6 md:px-12 mb-32" data-id="element-367">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-id="element-368">
          {filteredPosts.map((post, index) => <motion.div key={post.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="group flex flex-col h-full" data-id="element-369">
              <Link to={`/blog/${post.slug}`} className="block overflow-hidden mb-6 aspect-[3/2]" data-id="element-370">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-371" />
              </Link>
              <div className="flex items-center gap-4 mb-3 text-xs" data-id="element-372">
                <span className="text-robeta-copper font-bold uppercase tracking-widest" data-id="element-373">
                  {post.category}
                </span>
                <span className="text-robeta-black/40 uppercase tracking-widest" data-id="element-374">
                  {post.date}
                </span>
              </div>
              <Link to={`/blog/${post.slug}`} className="block mb-3" data-id="element-375">
                <h2 className="text-2xl font-bold text-robeta-black uppercase tracking-editorial leading-tight group-hover:text-robeta-copper transition-colors" data-id="element-376">
                  {post.title}
                </h2>
              </Link>
              <p className="text-robeta-black/60 font-light leading-relaxed mb-6 line-clamp-3 flex-grow" data-id="element-377">
                {post.excerpt}
              </p>
              <Link to={`/blog/${post.slug}`} className="inline-flex items-center text-xs font-bold uppercase tracking-widest text-robeta-black border-b border-robeta-black/20 pb-1 group-hover:border-robeta-copper transition-colors self-start" data-id="element-378">
                Read Article <ArrowRight className="ml-2 w-3 h-3" data-id="element-379" />
              </Link>
            </motion.div>)}
        </div>
        {filteredPosts.length === 0 && <div className="text-center py-20" data-id="element-380">
            <p className="text-robeta-black/40 font-light text-lg" data-id="element-381">
              No articles found in this category.
            </p>
          </div>}
      </section>

      <section className="py-24 bg-robeta-mist/10" data-id="element-382">
        <div className="container mx-auto px-6 md:px-12 text-center" data-id="element-383">
          <h2 className="text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-4" data-id="element-384">
            Never Miss a Story
          </h2>
          <p className="text-robeta-black/60 font-light mb-8 max-w-md mx-auto" data-id="element-385">
            Subscribe to our newsletter for the latest travel stories, design
            insights, and Robeta news.
          </p>
          <Link to="/contact" className="inline-block px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors" data-id="element-386">
            Subscribe Now
          </Link>
        </div>
      </section>
    </main>;
}